import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney, fmtInvNum, downloadCsv } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Printer, Pencil, MessageCircle, Info, Trash2, Mail, Share2, Download, Plus, RotateCcw, ScanBarcode, Camera, Search, X, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BulkSelectionBar, { useBulkSelection, SelectAllHeader, RowCheckbox } from "@/components/BulkSelectionBar";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";

export default function Invoices() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState(new URLSearchParams(window.location.search).get("kind") || "sale");
  const [waInv, setWaInv] = useState(null);
  const [emailInv, setEmailInv] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const openEmail = (inv) => {
    setEmailInv(inv);
    const saved = localStorage.getItem(`em_${inv.party_id || inv.party_name}`) || "";
    setEmailTo(saved);
  };
  const sendEmail = async () => {
    if (!emailInv || !emailTo.trim()) return toast.error("أدخل الإيميل");
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/invoices/${emailInv.id}/email`, { to_email: emailTo.trim() });
      localStorage.setItem(`em_${emailInv.party_id || emailInv.party_name}`, emailTo.trim());
      toast.success(data.message);
      setEmailInv(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSendingEmail(false); }
  };
  const [waPhone, setWaPhone] = useState("");
  const [invLookup, setInvLookup] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const nav = useNavigate();

  // Perform the actual invoice lookup and navigation (extracted so camera-scan handler can reuse it)
  const performLookup = async (num) => {
    if (!num) return;
    setLookupBusy(true);
    try {
      const searchKind = kind === "sale" || kind === "sale_return" ? "sale"
                        : kind === "purchase" || kind === "purchase_return" ? "purchase"
                        : null;
      const q = searchKind ? `?kind=${searchKind}` : "";
      const { data } = await api.get(`/invoices/by-number/${encodeURIComponent(num)}${q}`);
      if (!data) return toast.error("لم يتم العثور على الفاتورة");
      if (data.kind === "sale" || data.kind === "sale_return") nav(`/returns/sale?from=${data.id}`);
      else nav(`/returns/purchase?from=${data.id}`);
    } catch (er) { toast.error(er?.response?.status === 404 ? `لم يتم العثور على فاتورة رقم ${num}` : formatErr(er)); }
    finally { setLookupBusy(false); setInvLookup(""); }
  };

  const lookupToReturn = async (e) => {
    e?.preventDefault?.();
    await performLookup(invLookup.trim());
  };

  const onCameraScanned = async (raw) => {
    setScanOpen(false);
    // Barcodes may embed the invoice number as digits or as "#NUM"
    const num = String(raw || "").replace(/[^0-9]/g, "");
    if (!num) return toast.error("لم يُتعرَّف على رقم فاتورة صالح");
    await performLookup(num);
  };
  const { user, currentCompany } = useAuth();
  const canEdit = user?.role === "super_admin" || user?.role === "company_manager";
  const bulk = useBulkSelection(items.map(x => x.id));
  const [template, setTemplate] = useState({});
  const [search, setSearch] = useState(new URLSearchParams(window.location.search).get("focus") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  useEffect(() => { api.get("/company/template").then(({ data }) => setTemplate(data || {})).catch(() => {}); }, [currentCompany?.id]);

  const load = async () => {
    try {
      const params = { kind };
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/invoices", { params }); setItems(data); bulk.clear();
    }
    catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line
  // Debounced search by customer/supplier name or invoice number
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const del = async (inv) => {
    if (!confirm(`حذف الفاتورة رقم ${inv.number}؟ سيتم إعادة الأصناف للمخزون وتصحيح رصيد ${inv.kind==="sale"?"العميل":"المورد"}.`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("تم حذف الفاتورة وإعادة المخزون"); load(); }
    catch(e){ toast.error(formatErr(e)); }
  };

  const bulkDelete = async () => {
    const ids = bulk.selectedIds;
    if (!ids.length) return;
    if (!confirm(`حذف ${ids.length} فاتورة؟ سيتم إعادة الأصناف للمخزون وتصحيح أرصدة الأطراف.`)) return;
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      toast.success(`تم حذف ${data.deleted} فاتورة`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulkShare = () => {
    const ids = new Set(bulk.selectedIds);
    const rows = items.filter(i => ids.has(i.id));
    if (!rows.length) return;
    const total = rows.reduce((s, r) => s + (r.total || 0), 0);
    const lines = rows.map(r => `• #${r.number} · ${new Date(r.created_at).toLocaleDateString("ar-EG")} · ${r.party_name} · ${(r.total||0).toLocaleString("ar-EG")} ${r.currency}${r.remaining ? ` (باقي ${r.remaining.toLocaleString("ar-EG")})` : ""}`);
    const text = `*قائمة الفواتير (${rows.length})*\n\n${lines.join("\n")}\n\n*الإجمالي: ${total.toLocaleString("ar-EG")}*`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const bulkExportExcel = () => {
    const ids = new Set(bulk.selectedIds);
    const rows = items.filter(i => ids.has(i.id));
    if (!rows.length) return;
    const headers = ["الرقم", "التاريخ", "الطرف", "النوع", "الإجمالي", "المدفوع", "المتبقي", "العملة", "سعر الصرف", "أنشأها"];
    const csv = rows.map(r => [
      r.display_no || fmtInvNum(r.number), new Date(r.created_at).toLocaleString("ar-EG"), r.party_name,
      r.payment_type === "cash" ? "نقدي" : "آجل",
      r.total || 0, r.paid_amount || 0, r.remaining || 0,
      r.currency || "SYP", r.exchange_rate || 1, r.created_by_name || "",
    ]);
    // Totals grouped by each currency present in the exported invoices
    const byCur = {};
    rows.forEach(r => {
      const c = (r.currency || "SYP");
      if (!byCur[c]) byCur[c] = { total: 0, paid: 0, remaining: 0, count: 0 };
      byCur[c].total += Number(r.total) || 0;
      byCur[c].paid += Number(r.paid_amount) || 0;
      byCur[c].remaining += Number(r.remaining) || 0;
      byCur[c].count += 1;
    });
    const totalRows = Object.entries(byCur).map(([c, v]) => [
      `مجموع ${c} (${v.count} فاتورة)`, "", "", "",
      Number(v.total.toFixed(2)), Number(v.paid.toFixed(2)), Number(v.remaining.toFixed(2)), c, "", "",
    ]);
    downloadCsv(`invoices_${kind}_${new Date().toISOString().slice(0,10)}.csv`, [headers, ...csv, [], ...totalRows]);
    toast.success(`تم تصدير ${rows.length} فاتورة إلى Excel/CSV`);
  };

  const openWhatsApp = (inv) => {
    setWaInv(inv);
    // remember last phone for this party
    setWaPhone(localStorage.getItem(`wa_${inv.party_id||inv.party_name}`) || "");
  };

  const sendWhatsApp = () => {
    if (!waInv) return;
    const digits = (waPhone || "").replace(/\D/g, "");
    if (digits.length < 8) {
      toast.error("رقم الهاتف غير صحيح — أدخل الرقم مع رمز الدولة");
      return;
    }
    localStorage.setItem(`wa_${waInv.party_id||waInv.party_name}`, digits);
    const lines = waInv.lines.map(l => `• ${l.product_name} × ${l.quantity} = ${l.total.toLocaleString("ar-EG")}`).join("\n");
    const msg = [
      `*فاتورة ${waInv.kind==="sale"?"بيع":"شراء"} رقم ${waInv.number}*`,
      `التاريخ: ${new Date(waInv.created_at).toLocaleDateString("ar-EG")}`,
      `${waInv.kind==="sale"?"العميل":"المورد"}: ${waInv.party_name}`,
      "",
      "الأصناف:",
      lines,
      "",
      `الإجمالي: ${waInv.total.toLocaleString("ar-EG")} ${waInv.currency}`,
      waInv.paid_amount ? `المدفوع: ${waInv.paid_amount.toLocaleString("ar-EG")} ${waInv.currency}` : "",
      waInv.remaining ? `المتبقي: ${waInv.remaining.toLocaleString("ar-EG")} ${waInv.currency}` : "",
      "",
      "شكراً لتعاملكم معنا 🙏",
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
    setWaInv(null);
    setWaPhone("");
    toast.success("تم فتح واتساب — اضغط زر الإرسال داخل التطبيق");
  };

  const printInvoice = (inv, preview = false, requestedLayout = null) => {
    const w = window.open("", "_blank");
    const base = (currentCompany?.base_currency || "SYP").toUpperCase();
    const cur = (inv.currency || "SYP").toUpperCase();
    const fx = Number(inv.exchange_rate) || 1;
    const dual = cur !== base && fx > 0;
    const nf = (n) => Number(n || 0).toLocaleString("ar-EG", { maximumFractionDigits: 2 });
    const money = (x) => dual
      ? `<div class="dual"><div class="d-base tn">${nf(Number(x) * fx)} ${base}</div><div class="d-native tn">${nf(x)} ${cur}</div></div>`
      : `<span class="tn">${nf(x)} ${cur}</span>`;
    const rows = inv.lines.map(l => `<tr><td>${l.product_name}</td><td class="tn">${nf(l.quantity)}</td><td>${money(l.price)}</td><td>${money(l.total)}</td></tr>`).join("");
    // Barcode encodes the invoice number so the mobile camera can scan it to open the returns flow
    const barcodeVal = fmtInvNum(inv.number);
    const prefix = (template.invoice_prefix || "").trim();
    const invNumDisplay = prefix ? `${prefix}${inv.display_no || fmtInvNum(inv.number)}` : (inv.display_no || fmtInvNum(inv.number));
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
    const logoSrc = template.logo_url ? (template.logo_url.startsWith("http") ? template.logo_url : BACKEND + template.logo_url) : "";
    const primary = template.primary_color || "#1E3A8A";
    const layout = requestedLayout || template.invoice_layout || "classic";
    const design = template.invoice_design?.[layout] || {};
    const blockOrder = Array.isArray(design.order) ? design.order : ["logo", "company", "header", "invoice", "details", "barcode", "items", "totals", "notes", "terms", "footer"];
    const hiddenBlocks = Array.isArray(design.hidden) ? design.hidden : [];
    const blockStyles = design.blockStyles || {};
    const sectionGap = Math.max(0, Math.min(40, Number(design.spacing ?? 10)));
    const block = (id, html) => {
      const cfg = blockStyles[id] || {};
      const align = ["left", "center", "right"].includes(cfg.align) ? cfg.align : "right";
      const size = Math.max(8, Math.min(32, Number(cfg.fontSize || design.fontSize || 12)));
      return `<section class="designer-block designer-${id}" style="text-align:${align};font-size:${size}px;margin-bottom:${sectionGap}px">${html}</section>`;
    };
    const sections = {
      logo: logoSrc ? block("logo", `<img src="${logoSrc}" alt="logo" style="max-height:${Math.max(30, Math.min(500, Number(design.logoHeight || 70)))}px;max-width:${Math.max(30, Math.min(500, Number(design.logoWidth || 150)))}px;object-fit:contain" />`) : "",
      company: currentCompany ? block("company", `<div style="font-size:16pt;font-weight:800;color:#0f766e;margin-bottom:2px">${currentCompany.name||""}</div>${(template.show_tax_id && (template.tax_id || currentCompany.vat_number)) ? `<div class="supp-num">الرقم الضريبي: <strong>${template.tax_id || currentCompany.vat_number}</strong></div>` : ""}${currentCompany.address ? `<div class="supp-num">${currentCompany.address}</div>` : ""}${currentCompany.phone ? `<div class="supp-num">هاتف: ${currentCompany.phone}</div>` : ""}`) : "",
      header: template.header_text ? block("header", `<div class="supp-num">${String(template.header_text).replace(/</g,"&lt;")}</div>`) : "",
      invoice: block("invoice", `<h1>فاتورة ${inv.kind==="sale"?"بيع":inv.kind==="purchase"?"شراء":inv.kind==="sale_return"?"مرتجع مبيعات":"مرتجع مشتريات"} — رقم ${invNumDisplay}</h1>`),
      details: block("details", `<div>التاريخ: ${new Date(inv.created_at).toLocaleString("ar-EG")}</div><div>${inv.kind==="sale"||inv.kind==="sale_return"?"العميل":"المورد"}: <strong>${inv.party_name}</strong></div><div>نوع الدفع: ${inv.payment_type==="cash"?"نقدي":"آجل"}</div>${inv.supplier_invoice_number ? `<div class="supp-num">رقم فاتورة المورد: <strong>${inv.supplier_invoice_number}</strong></div>` : ""}`),
      barcode: block("barcode", `<div class="barcode-box"><svg id="inv-barcode"></svg><div class="lbl">امسح لإنشاء مرتجع</div></div>`),
      items: block("items", `<table><thead><tr><th>الصنف</th><th>الكمية</th><th>السعر</th><th>الإجمالي</th></tr></thead><tbody>${rows}</tbody></table>`),
      totals: block("totals", `<div style="margin-top:12px"><div class="sum"><span>الإجمالي الفرعي</span>${money(inv.subtotal||inv.total)}</div>${inv.discount_total ? `<div class="sum"><span>الخصم</span>${money(inv.discount_total)}</div>` : ""}${inv.vat_amount ? `<div class="sum"><span>ضريبة القيمة المضافة (${inv.vat_rate}%)</span>${money(inv.vat_amount)}</div>` : ""}<div class="total">${dual ? `الإجمالي: ${nf(inv.total*fx)} ${base} / ${nf(inv.total)} ${cur}` : `الإجمالي: ${nf(inv.total)} ${cur}`}</div>${dual ? `<div class="supp-num" style="margin-top:4px">سعر الصرف المعتمد: 1 ${base} = ${nf(1/fx)} ${cur}</div>` : ""}<div class="sum"><span>المدفوع</span>${money(inv.paid_amount||0)}</div>${inv.remaining ? `<div class="sum" style="color:#DC2626"><span><strong>المتبقي</strong></span>${money(inv.remaining)}</div>` : ""}</div>`),
      notes: inv.notes ? block("notes", `<div class="notes"><span class="lbl">ملاحظات:</span>${inv.notes.replace(/</g,"&lt;")}</div>`) : "",
      terms: template.payment_terms ? block("terms", `<div class="notes" style="background:#EFF6FF;border-right-color:#3B82F6"><span class="lbl" style="color:#1E3A8A">شروط الدفع:</span>${String(template.payment_terms).replace(/</g,"&lt;")}</div>`) : "",
      footer: template.footer_text ? block("footer", `<div style="border-top:1px solid #cbd5e1;padding-top:8px">${String(template.footer_text).replace(/</g,"&lt;")}</div>`) : "",
    };
    const invoiceHtml = blockOrder.filter(id => sections[id] && !hiddenBlocks.includes(id)).map(id => sections[id]).join("");
    w.document.write(`
      <html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>فاتورة ${invNumDisplay}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet">
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
      <style>
        @page { size: ${layout === "thermal" ? "80mm auto" : "A4"}; margin: ${layout === "thermal" ? "4mm" : `${Math.max(0, Math.min(35, Number(design.pageMargin ?? 12)))}mm`}; }
        *{box-sizing:border-box}
        html,body{
          font-family:'Amiri','Cairo','Tajawal',serif;
          padding:20px;color:#000;direction:rtl;text-align:right;
          -webkit-print-color-adjust:exact;print-color-adjust:exact;
          font-size:${Math.max(8, Math.min(24, Number(design.fontSize || 12)))}pt;
          padding:${layout === "thermal" ? "0" : `${Math.max(0, Math.min(35, Number(design.pageMargin ?? 12)))}px`};
        }
        .designer-block{width:100%;break-inside:avoid}
        h1{color:${primary};margin:0 0 4px;font-family:'Cairo','Amiri',sans-serif}
        table{width:100%;border-collapse:collapse;margin-top:12px;direction:rtl}
        thead{display:table-header-group}
        tr{page-break-inside:avoid}
        th,td{border:1px solid #333;padding:8px 10px;text-align:right;vertical-align:middle}
        th{background:#E5E7EB;font-weight:700;color:#000}
        .tn{font-variant-numeric:tabular-nums;direction:ltr;unicode-bidi:embed;text-align:left}
        .total{font-size:20px;font-weight:bold;text-align:end;margin-top:12px}
        .sum{display:flex;justify-content:space-between;align-items:flex-start;padding:4px 0;border-bottom:1px dashed #94a3b8}
        .dual{display:flex;flex-direction:column;line-height:1.2;align-items:flex-start}
        .d-base{font-weight:700}
        .d-native{font-size:9pt;color:#555}
        .header{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-direction:row-reverse}
        .barcode-box{text-align:center;border:1px dashed #94a3b8;padding:6px 10px;border-radius:6px}
        .barcode-box svg{max-width:180px;height:50px}
        .barcode-box .lbl{font-size:9px;color:#64748b;margin-top:2px}
        .notes{margin-top:14px;padding:10px;background:#FEF3C7;border-right:4px solid #F59E0B;border-radius:4px;font-size:13px;line-height:1.6;white-space:pre-wrap;word-break:break-word}
        .notes .lbl{font-weight:bold;color:#78350F;margin-bottom:4px;display:block}
        .supp-num{font-size:12px;color:#475569;margin-top:2px}
        .layout-modern h1{color:#0f766e}.layout-modern th{background:#0f766e;color:#fff}.layout-modern .notes{border-right-color:#0f766e;background:#f0fdfa}
        .layout-simple h1{color:#2563eb}.layout-simple th{background:#eff6ff;color:#1e40af;border-bottom:2px solid #93c5fd}.layout-simple .total{color:#2563eb}
        .layout-thermal{width:72mm;margin:0 auto;padding:0;font-family:Arial,sans-serif;font-size:10px}.layout-thermal .header{display:block;text-align:center}.layout-thermal .header h1{font-size:16px;text-align:center}.layout-thermal .barcode-box{margin-top:6px}.layout-thermal table{font-size:9px}.layout-thermal th,.layout-thermal td{padding:4px 3px;border:0;border-bottom:1px dashed #999}.layout-thermal th{background:#fff;color:#000}.layout-thermal .total{font-size:16px;border-top:2px solid #000}.layout-thermal .notes{background:#fff;border-right:0;border-top:1px dashed #999;border-radius:0;padding:6px}.layout-thermal .dual{font-size:9px}
        .preview-toolbar{position:sticky;top:0;z-index:5;display:flex;gap:8px;justify-content:center;padding:10px;background:#0f172a;color:#fff;margin:-20px -20px 18px;font-family:Arial,sans-serif}.preview-toolbar button{border:0;border-radius:6px;padding:8px 16px;cursor:pointer;font-weight:700}.preview-toolbar .print{background:#0f766e;color:#fff}.preview-toolbar .close{background:#334155;color:#fff}
        @media print{
          html,body{background:#fff!important;color:#000!important}
          .preview-toolbar{display:none!important}
        }
      </style></head><body class="layout-${layout}">
      ${preview ? `<div class="preview-toolbar"><button class="print" onclick="window.print()">طباعة الفاتورة</button><button class="close" onclick="window.close()">إغلاق المعاينة</button></div>` : ""}
      ${invoiceHtml}
      <script>
        window.onload = () => {
          try {
            const doPrint = () => {
              if (${preview ? "false" : "true"} && document.fonts && document.fonts.ready) {
                document.fonts.ready.then(() => setTimeout(() => window.print(), 250));
              } else if (!${preview ? "true" : "false"}) {
                setTimeout(() => window.print(), 700);
              }
            };
            if (window.JsBarcode) {
              JsBarcode("#inv-barcode", ${JSON.stringify(barcodeVal)}, { format: "CODE128", displayValue: true, fontSize: 12, height: 40, margin: 2 });
              doPrint();
            } else {
              // Poll for CDN to load, up to 3s
              let tries = 0;
              const iv = setInterval(() => {
                tries++;
                if (window.JsBarcode) {
                  clearInterval(iv);
                  try { JsBarcode("#inv-barcode", ${JSON.stringify(barcodeVal)}, { format: "CODE128", displayValue: true, fontSize: 12, height: 40, margin: 2 }); } catch(e) {}
                  doPrint();
                } else if (tries > 30) { clearInterval(iv); doPrint(); }
              }, 100);
            }
          } catch(e) { window.print(); }
        };
      </script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">{t("invoices_title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("invoices_subtitle")}</p>
      </div>
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="sale" data-testid="tab-sales">{t("tab_sales")}</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="tab-purchases">{t("tab_purchases")}</TabsTrigger>
          <TabsTrigger value="sale_return" data-testid="tab-sale-returns">{t("tab_sale_returns")}</TabsTrigger>
          <TabsTrigger value="purchase_return" data-testid="tab-purchase-returns">{t("tab_purchase_returns")}</TabsTrigger>
        </TabsList>
      </Tabs>
      {/* Search invoices by customer/supplier name or number */}
      <div className="relative max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={kind === "sale" || kind === "sale_return" ? "ابحث باسم العميل أو رقم الفاتورة..." : "ابحث باسم المورد أو رقم الفاتورة..."}
          className="ps-9 pe-9 h-9"
          data-testid="invoice-search-input"
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="invoice-search-clear" title="مسح">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Filter invoices by date range */}
      <div className="flex flex-wrap items-end gap-2" data-testid="invoice-date-filter">
        <div>
          <Label className="text-[11px] text-muted-foreground">من تاريخ</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="invoice-date-from" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">إلى تاريخ</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="invoice-date-to" />
        </div>
        {(() => {
          const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const today = fmtD(new Date());
          const first = fmtD(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
          return (
            <div className="flex items-center gap-1.5">
              <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => { setDateFrom(today); setDateTo(today); }} data-testid="invoice-date-today">اليوم</Button>
              <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => { setDateFrom(first); setDateTo(today); }} data-testid="invoice-date-month">هذا الشهر</Button>
              {(dateFrom || dateTo) && (
                <Button type="button" variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="invoice-date-clear"><X className="w-3.5 h-3.5 me-1" /> مسح التاريخ</Button>
              )}
            </div>
          );
        })()}
        {(dateFrom || dateTo) && <span className="text-xs text-muted-foreground self-center" data-testid="invoice-date-count">النتائج: {items.length} فاتورة</span>}
      </div>
      {/* Create-new bar per tab + Barcode-to-Return lookup */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <form onSubmit={lookupToReturn} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <ScanBarcode className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-600" />
            <Input
              value={invLookup}
              onChange={(e)=>setInvLookup(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="امسح باركود / أدخل رقم الفاتورة للانتقال إلى إنشاء مرتجع منها..."
              className="ps-9 h-9 font-mono"
              data-testid="inv-lookup-input"
              inputMode="numeric"
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={()=>setScanOpen(true)} className="gap-1 shrink-0" data-testid="inv-lookup-camera-btn" title="مسح باركود بالكاميرا">
            <Camera className="w-3.5 h-3.5" />
          </Button>
          <Button type="submit" variant="outline" size="sm" disabled={lookupBusy || !invLookup} className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0" data-testid="inv-lookup-btn">
            <RotateCcw className="w-3.5 h-3.5" />
            {lookupBusy ? "..." : "مرتجع"}
          </Button>
        </form>
        <div className="flex items-center gap-2">
          {kind === "sale_return" && (
            <Button onClick={()=>nav("/returns/sale")} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="new-sale-return-btn">
              <RotateCcw className="w-4 h-4" /> إنشاء مرتجع مبيعات
            </Button>
          )}
          {kind === "purchase_return" && (
            <Button onClick={()=>nav("/returns/purchase")} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="new-purchase-return-btn">
              <RotateCcw className="w-4 h-4" /> إنشاء مرتجع مشتريات
            </Button>
          )}
        </div>
      </div>
      <div className="border border-border rounded-md bg-card overflow-hidden">
        {canEdit && (
          <div className="px-3 pt-3">
            <BulkSelectionBar bulk={bulk} label="فاتورة">
              <Button size="sm" variant="outline" onClick={bulkExportExcel} className="gap-1 border-slate-400 text-slate-700 hover:bg-slate-50" data-testid="bulk-export-inv-btn"><Download className="w-3.5 h-3.5" /> تصدير Excel</Button>
              <Button size="sm" variant="outline" onClick={bulkShare} className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-share-inv-btn"><Share2 className="w-3.5 h-3.5" /> مشاركة</Button>
              <Button size="sm" variant="destructive" onClick={bulkDelete} className="gap-1" data-testid="bulk-delete-inv-btn"><Trash2 className="w-3.5 h-3.5" /> حذف المحدد</Button>
            </BulkSelectionBar>
          </div>
        )}
        <Table>
          <TableHeader><TableRow>
            {canEdit && <TableHead className="w-10"><SelectAllHeader bulk={bulk} /></TableHead>}
            <TableHead className="text-start">{t("col_number")}</TableHead>
            <TableHead className="text-start">{t("col_date")}</TableHead>
            <TableHead className="text-start">{kind==="sale"?t("col_customer"):t("col_supplier")}</TableHead>
            <TableHead className="text-start">{t("col_type")}</TableHead>
            <TableHead className="text-start">{t("col_total")}</TableHead>
            <TableHead className="text-start">{t("col_remaining")}</TableHead>
            <TableHead className="text-start">{t("col_fx")}</TableHead>
            <TableHead className="text-start">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={canEdit ? 9 : 8} className="text-center py-8 text-muted-foreground">{t("no_invoices")}</TableCell></TableRow>}
            {items.map(i => (
              <TableRow key={i.id} data-testid={`invoice-row-${i.id}`} className={canEdit && bulk.isSelected(i.id) ? "bg-primary/5" : ""}>
                {canEdit && <TableCell><RowCheckbox bulk={bulk} id={i.id} /></TableCell>}
                <TableCell className="font-mono">
                  <div>#{i.display_no || fmtInvNum(i.number)}</div>
                  {i.supplier_invoice_number && <div className="text-[10px] text-muted-foreground">مورد: {i.supplier_invoice_number}</div>}
                </TableCell>
                <TableCell className="text-xs">{new Date(i.created_at).toLocaleString("ar-EG")}</TableCell>
                <TableCell className="font-semibold">{i.party_name}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${i.payment_type==="cash"?"bg-emerald-50 text-emerald-700":"bg-amber-50 text-amber-700"}`}>{i.payment_type==="cash"?t("cash"):t("credit")}</span></TableCell>
                <TableCell className="tabular-nums font-bold">{fmtMoney(i.total, i.currency)}</TableCell>
                <TableCell className={`tabular-nums ${(i.remaining||0)>0?"text-destructive font-bold":"text-muted-foreground"}`}>{i.remaining ? fmtMoney(i.remaining, i.currency) : "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground tabular-nums" data-testid={`fx-${i.id}`}>
                  {i.exchange_rate && +i.exchange_rate !== 1 ? `1 ${i.currency} = ${(+i.exchange_rate).toLocaleString("ar-EG")}` : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {canEdit && <Button size="sm" variant="ghost" onClick={()=>nav(`/invoices/${i.id}/edit`)} data-testid={`edit-invoice-${i.id}`} title="تعديل الفاتورة"><Pencil className="w-4 h-4" /></Button>}
                    {(kind === "sale" || kind === "purchase") && canEdit && (
                      <Button size="sm" variant="ghost" onClick={()=>nav(`/returns/${kind === "sale" ? "sale" : "purchase"}?from=${i.id}`)} className="text-amber-700" data-testid={`return-invoice-${i.id}`} title="إنشاء مرتجع من هذه الفاتورة">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={()=>openWhatsApp(i)} className="text-emerald-600" data-testid={`whatsapp-invoice-${i.id}`} title="مشاركة عبر واتساب"><MessageCircle className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>openEmail(i)} className="text-blue-600" data-testid={`email-invoice-${i.id}`} title="إرسال بالإيميل"><Mail className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>printInvoice(i, true)} className="text-violet-600" data-testid={`preview-invoice-${i.id}`} title="معاينة الفاتورة"><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>printInvoice(i)} data-testid={`print-invoice-${i.id}`} title="طباعة الفاتورة"><Printer className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>printInvoice(i, false, "thermal")} className="gap-1 text-teal-700" data-testid={`print-thermal-invoice-${i.id}`} title="طباعة حرارية بعرض 80 مم" aria-label={`طباعة الفاتورة ${i.display_no || fmtInvNum(i.number)} حراريًا بعرض 80 مم`}><Printer className="w-4 h-4" /><span className="text-[10px] font-bold">80 مم</span></Button>
                    {canEdit && <Button size="sm" variant="ghost" className="text-destructive" onClick={()=>del(i)} data-testid={`delete-invoice-${i.id}`} title="حذف الفاتورة"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!waInv} onOpenChange={(o)=>!o && setWaInv(null)}>
        <DialogContent className="max-w-md" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <MessageCircle className="w-5 h-5" /> {t("share_whatsapp")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("wa_phone_label")}</Label>
              <Input value={waPhone} onChange={(e)=>setWaPhone(e.target.value)} placeholder="مثال: 963991234567" data-testid="whatsapp-phone-input" className="tabular-nums text-lg font-mono h-11 mt-1" dir="ltr" />
              <div className="text-xs text-muted-foreground mt-1">أمثلة: <b>963</b> سوريا · <b>90</b> تركيا · <b>971</b> الإمارات · <b>966</b> السعودية</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs text-amber-900 flex gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <b>ملاحظة:</b> بعد الضغط على «فتح واتساب»، سيفتح تطبيق واتساب مع الرسالة جاهزة —
                <b> اضغط زر الإرسال داخل الواتساب</b> لإكمال الإرسال (شرط من واتساب).
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setWaInv(null)}>{t("cancel")}</Button>
            <Button onClick={sendWhatsApp} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-send-button">
              <MessageCircle className="w-4 h-4 me-1" /> {t("wa_open")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera scanner for invoice barcode lookup */}
      <BarcodeScannerDialog open={scanOpen} onClose={()=>setScanOpen(false)} onScan={onCameraScanned} />

      {/* Email dialog */}
      <Dialog open={!!emailInv} onOpenChange={(o)=>!o && setEmailInv(null)}>        <DialogContent className="max-w-md" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Mail className="w-5 h-5" /> إرسال الفاتورة بالإيميل
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>إيميل العميل</Label>
              <Input type="email" value={emailTo} onChange={(e)=>setEmailTo(e.target.value)} placeholder="customer@example.com" data-testid="email-to-input" className="mt-1" />
            </div>
            {emailInv && (
              <div className="text-xs text-muted-foreground p-3 bg-muted rounded-md">
                <div>سيتم إرسال فاتورة <span className="font-bold">#{emailInv.number}</span> للعميل <span className="font-bold">{emailInv.party_name}</span></div>
                <div>الإجمالي: {emailInv.total?.toLocaleString("ar-EG")} {emailInv.currency}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEmailInv(null)}>إلغاء</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} className="bg-blue-600 hover:bg-blue-700" data-testid="email-send-button">
              <Mail className="w-4 h-4 me-1" /> {sendingEmail ? "جاري الإرسال..." : "إرسال"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
